/**
 * BGM の「曲の詳細」(ジャンル / ムード / BPM / キー) の入力欄。
 * シナリオ編集画面の BGM セクションと、全編モーションの BGM 設定モーダル (生成タブ) で共用する。
 * 歌詞モードのジャンルは選択式なので、genreSelect でドロップダウンを差し込み、showGenreInput で自由入力欄の有無を切り替える。
 */
import type { ReactNode } from 'react';
import styles from './ScenarioGenerator.module.css';

export interface BgmSongDetailValue {
  genre?: string;
  mood?: string;
  bpm?: number | null;
  musical_key?: string;
}

const FIELD_STYLE = { display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '13px', color: 'rgba(245, 241, 230, 0.5)' } as const;
const INPUT_STYLE = { boxSizing: 'border-box', fontSize: '13px', padding: '4px 6px', borderRadius: '4px', border: '1px solid rgba(245, 241, 230, 0.15)', background: 'rgba(0, 0, 0, 0.2)', color: 'rgba(245, 241, 230, 0.85)' } as const;

export function BgmSongDetailFields({
  value, onChange, disabled, genreSelect, showGenreInput = true, genreInputLabel = 'ジャンル', bpmMin = 40, bpmMax = 220,
}: {
  value: BgmSongDetailValue;
  onChange: (patch: Partial<BgmSongDetailValue>) => void;
  disabled?: boolean;
  /** ジャンルの選択式コントロール (歌詞モード)。自由入力欄の上に置く */
  genreSelect?: ReactNode;
  showGenreInput?: boolean;
  genreInputLabel?: string;
  bpmMin?: number;
  bpmMax?: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
      <div style={{ ...FIELD_STYLE, flex: '1 1 140px', minWidth: 0, maxWidth: '200px' }}>
        <span>ジャンル</span>
        {genreSelect}
        {showGenreInput && (
          <input
            className={styles.bgmDetailControl}
            type="text"
            aria-label={genreInputLabel}
            value={value.genre || ''}
            onChange={(e) => onChange({ genre: e.target.value })}
            placeholder="Lo-fi hip hop"
            disabled={disabled}
            style={{ ...INPUT_STYLE, width: '100%' }}
          />
        )}
      </div>
      <label style={{ ...FIELD_STYLE, flex: '1 1 140px', minWidth: 0, maxWidth: '200px' }}>
        ムード
        <input
          className={styles.bgmDetailControl}
          type="text"
          value={value.mood || ''}
          onChange={(e) => onChange({ mood: e.target.value })}
          placeholder="warm, nostalgic"
          disabled={disabled}
          style={{ ...INPUT_STYLE, width: '100%' }}
        />
      </label>
      <label style={{ ...FIELD_STYLE, flex: '0 0 auto' }}>
        BPM
        <input
          className={styles.bgmDetailControl}
          type="number"
          value={value.bpm ?? ''}
          onChange={(e) => {
            const v = e.target.value === '' ? null : Number(e.target.value);
            onChange({ bpm: v != null && Number.isFinite(v) ? v : null });
          }}
          placeholder="90"
          min={bpmMin}
          max={bpmMax}
          disabled={disabled}
          style={{ ...INPUT_STYLE, width: '56px' }}
        />
      </label>
      <label style={{ ...FIELD_STYLE, flex: '0 0 auto' }}>
        キー
        <input
          className={styles.bgmDetailControl}
          type="text"
          value={value.musical_key || ''}
          onChange={(e) => onChange({ musical_key: e.target.value })}
          placeholder="C major"
          disabled={disabled}
          style={{ ...INPUT_STYLE, width: '80px' }}
        />
      </label>
    </div>
  );
}
